// Net-worth accounting for the end-of-game standings screen.
//
// The totals here deliberately mirror `getWinnerOfTheGame` (gameHelpers.ts) —
// cash + stock value + property face value + house value — so the breakdown the
// player reads always adds up to the number that decided the game. Mortgaged
// properties still count at full face value there, so they do here too; they're
// flagged per property instead, in the detailed view.
import { mapDesigns } from "@mono/mapDesigns"

/** One owned property, with what it is worth and what it earned. */
export interface EndGamePropertyBreakdown {
  order: number
  name: string
  /** Group colour, for the little swatch in the detailed list. */
  color?: string
  price: number
  houses: number
  /** houses === 5 is a hotel, same convention the board uses. */
  isHotel: boolean
  houseValue: number
  mortgaged: boolean
  /** Rent this owner collected on this property over the whole game. */
  rentEarned: number
}

/** A player's whole balance sheet at the final whistle. */
export interface EndGamePlayerWorth {
  order: PlayerOrder
  playerId: string
  name: string
  isBankrupt: boolean
  cash: number
  stockShares: number
  stockMultiplier: number
  stockValue: number
  propertyCount: number
  propertyValue: number
  houseCount: number
  hotelCount: number
  houseValue: number
  totalWorth: number
  /** Total rent collected across every property they owned when it was paid. */
  rentEarned: number
  /** Cash handed to the bank for properties (direct buys + auctions won). */
  spentOnProperties: number
  properties: EndGamePropertyBreakdown[]
}

const rentKey = (owner: number, property: number) => `${owner}:${property}`

/**
 * Walks the situation once and returns one balance sheet per player, richest
 * first. Rent is attributed by the owner recorded on each rent-paid log item,
 * so a property that changed hands credits each owner only for the rent it
 * collected while they held it. Legacy log items carry no owner and are skipped.
 */
export const buildNetWorthBreakdown = (
  game?: IGameDetails | null
): EndGamePlayerWorth[] => {
  const situation = game?.situation
  const map = mapDesigns.find((item) => item.id === game?.settings?.mapId)
  if (!situation || !map) return []

  const cellOf = (propertyOrder: number) =>
    map.cells.find((cell) => cell.order === propertyOrder)

  const groupOf = (propertyOrder: number) => {
    const groupId = cellOf(propertyOrder)?.propertyDetails?.groupId
    return groupId ? map.groups.find((group) => group.id === groupId) : undefined
  }

  const rentEarned: Record<string, number> = {}
  const spentOnProperties: Record<number, number> = {}

  ;(situation.historyItems || []).forEach((item) => {
    switch (item.type) {
      case "rent-paid": {
        if (item.ownerOrder === undefined) break
        const key = rentKey(item.ownerOrder, item.propertyOrder)
        rentEarned[key] = (rentEarned[key] || 0) + item.rent
        break
      }
      case "private-buy":
      case "auction-won": {
        spentOnProperties[item.playerOrder] =
          (spentOnProperties[item.playerOrder] || 0) + item.amount
        break
      }
    }
  })

  const sheets = situation.players.map((player) => {
    const properties: EndGamePropertyBreakdown[] = player.properties
      .map((propertyOrder) => {
        const details = cellOf(propertyOrder)?.propertyDetails
        if (!details) return null
        const houses =
          situation.housesData?.find(
            (data) => data.propertyOrder === propertyOrder
          )?.houses || 0
        const housePrice = groupOf(propertyOrder)?.housePrice || 0

        return {
          order: propertyOrder,
          name: details.name,
          color: groupOf(propertyOrder)?.color,
          price: details.price || 0,
          houses,
          isHotel: houses === 5,
          houseValue: housePrice * houses,
          mortgaged: !!situation.mortgagedProperties?.includes(propertyOrder),
          rentEarned: rentEarned[rentKey(player.order, propertyOrder)] || 0,
        } as EndGamePropertyBreakdown
      })
      .filter((item): item is EndGamePropertyBreakdown => !!item)

    const sum = (pick: (item: EndGamePropertyBreakdown) => number) =>
      properties.reduce((total, item) => total + pick(item), 0)

    const cash = player.money
    const stockValue = player.stock * player.stockMultiplier
    const propertyValue = sum((item) => item.price)
    const houseValue = sum((item) => item.houseValue)

    return {
      order: player.order,
      playerId: player.id,
      name: game?.players.find((user) => user.email === player.id)?.username || "",
      isBankrupt: player.isBankrupt,
      cash,
      stockShares: player.stock,
      stockMultiplier: player.stockMultiplier,
      stockValue,
      propertyCount: properties.length,
      propertyValue,
      houseCount: sum((item) => (item.isHotel ? 0 : item.houses)),
      hotelCount: properties.filter((item) => item.isHotel).length,
      houseValue,
      totalWorth: cash + stockValue + propertyValue + houseValue,
      rentEarned: sum((item) => item.rentEarned),
      spentOnProperties: spentOnProperties[player.order] || 0,
      properties: [...properties].sort(
        (a, b) => b.price + b.houseValue - (a.price + a.houseValue)
      ),
    }
  })

  return sheets.sort((a, b) => b.totalWorth - a.totalWorth)
}

/** The four buckets a net worth is made of, in the order they're always shown. */
export const WORTH_SEGMENTS = [
  { key: "cash", label: "Cash", color: "#34d399" },
  { key: "stockValue", label: "Stocks", color: "#38bdf8" },
  { key: "propertyValue", label: "Properties", color: "#a78bfa" },
  { key: "houseValue", label: "Houses", color: "#fbbf24" },
] as const

export type WorthSegmentKey = (typeof WORTH_SEGMENTS)[number]["key"]
